import { getOllieApiError } from 'src/features/OllieSlice'
import { API } from './interceptor'

export const OLLIE_ORDERING_API_BASE = 'ollie/ordering'

export const OLLIE_ORDERING_VOICE_FAILED_MESSAGE =
  'Ollie could not hear that. Type what you need instead.'
export const OLLIE_ORDERING_VOICE_OFFLINE_MESSAGE =
  'Ollie could not be reached. Check your signal, then try again or type it.'

/** A clip that has not landed in twenty seconds is not coming back. */
export const OLLIE_ORDERING_VOICE_TIMEOUT_MS = 20000

const EXPIRED_SESSION_CODES = new Set([
  'OLLIE_ORDERING_SESSION_EXPIRED',
  'OLLIE_ORDERING_SESSION_INACTIVE',
])

export function isOllieOrderingSessionExpiredError(error) {
  const code = error?.code || error?.response?.data?.code
  return EXPIRED_SESSION_CODES.has(code)
}

async function postOllieOrdering(path, body) {
  return API.post('api2', `${OLLIE_ORDERING_API_BASE}/${path}`, { body })
}

/**
 * Start or resume a proposed-order session. Does not count an Ollie use.
 */
export async function startOllieOrderingSession({
  venueId,
  platform = 'dashboard',
} = {}) {
  if (!venueId) {
    throw new Error('venueId is required')
  }

  const data = await postOllieOrdering('sessions', { venueId, platform })
  if (!data?.session?.id && !data?.session?._id) {
    throw new Error(data?.message || 'Could not start an Ollie order session')
  }

  return {
    session: {
      id: String(data.session.id || data.session._id),
      venueId: data.session.venueId || venueId,
      status: data.session.status,
    },
    usage: data.usage || null,
    resumed: Boolean(data.resumed),
  }
}

/**
 * Interpret chef text into a stored proposal. Counts one Ollie use for the
 * search, not per product. Does not write the basket.
 */
export async function interpretOllieOrderingText({
  venueId,
  sessionId,
  text,
} = {}) {
  if (!venueId) {
    throw new Error('venueId is required')
  }
  if (!sessionId) {
    throw new Error('sessionId is required')
  }

  const data = await postOllieOrdering(`sessions/${sessionId}/interpret`, {
    venueId,
    text,
    platform: 'dashboard',
  })

  if (!data?.proposal) {
    throw new Error(data?.message || 'Ollie could not build a proposed order')
  }

  return {
    interpretation: data.interpretation || null,
    proposal: data.proposal,
    usage: data.usage || null,
  }
}

/**
 * Apply a chef correction to the stored proposal.
 * Structured picks, quantity edits, and removes do not count a use
 * and do not write the basket.
 */
export async function correctOllieOrderingProposal({
  venueId,
  sessionId,
  text,
  pick,
  edit,
} = {}) {
  if (!venueId) {
    throw new Error('venueId is required')
  }
  if (!sessionId) {
    throw new Error('sessionId is required')
  }

  const body = {
    venueId,
    platform: 'dashboard',
  }
  if (pick?.choice) {
    body.pick = {
      phrase: pick.phrase || null,
      choice: String(pick.choice),
    }
  } else if (edit?.remove) {
    body.edit = {
      phrase: edit.phrase || null,
      remove: true,
    }
  } else if (edit?.quantity != null) {
    body.edit = {
      phrase: edit.phrase || null,
      quantity: Number(edit.quantity),
    }
  } else {
    body.text = text
  }

  const data = await postOllieOrdering(`sessions/${sessionId}/correct`, body)
  if (!data?.proposal) {
    throw new Error(
      data?.message || 'Ollie could not update the proposed order',
    )
  }

  return {
    proposal: data.proposal,
    correction: data.correction || null,
  }
}

export function ollieOrderingBasketPath(venueId) {
  return `/v/${venueId}/purchasing/basket`
}

/**
 * Write accepted proposal lines into the existing OMS basket.
 * Does not count an Ollie use. Does not submit the supplier order.
 * Client lines are ignored — Express confirms the stored proposal.
 */
export async function confirmOllieOrderingProposal({
  venueId,
  sessionId,
  proposalUpdatedAt,
} = {}) {
  if (!venueId) {
    throw new Error('venueId is required')
  }
  if (!sessionId) {
    throw new Error('sessionId is required')
  }

  const body = {
    venueId,
    platform: 'dashboard',
  }
  if (proposalUpdatedAt) {
    body.proposalUpdatedAt = proposalUpdatedAt
  }

  const data = await postOllieOrdering(`sessions/${sessionId}/confirm`, body)
  if (!data?.success) {
    throw new Error(data?.message || 'Ollie could not add this to the basket')
  }

  return {
    alreadyConfirmed: Boolean(data.alreadyConfirmed),
    proposal: data.proposal || null,
    written: data.written || [],
    skipped: data.skipped || [],
    session: data.session || null,
  }
}

/**
 * Speech to text only. Caller then uses the same interpret API as typing.
 */
export async function transcribeOllieOrderingAudio({
  venueId,
  blob,
  filename = 'order.webm',
} = {}) {
  if (!venueId) {
    throw new Error('venueId is required')
  }
  if (!blob) {
    throw new Error('Record a short clip, then stop the mic')
  }

  const body = new FormData()
  body.append('audio', blob, filename)
  body.append('venueId', venueId)

  try {
    const data = await API.post(
      'api2',
      `${OLLIE_ORDERING_API_BASE}/transcribe?venueId=${encodeURIComponent(
        venueId,
      )}`,
      { body, timeout: OLLIE_ORDERING_VOICE_TIMEOUT_MS },
    )
    const text = String(data?.text || '').trim()
    if (!text) {
      throw new Error(OLLIE_ORDERING_VOICE_FAILED_MESSAGE)
    }
    return text
  } catch (error) {
    if (error?.code === 'ECONNABORTED' || error?.message === 'Network Error') {
      // A clip that never lands is the signal, not the chef's voice.
      throw new Error(OLLIE_ORDERING_VOICE_OFFLINE_MESSAGE)
    }
    if (error?.message && !error?.response) {
      throw error
    }
    throw new Error(
      getOllieApiError(error).message || OLLIE_ORDERING_VOICE_FAILED_MESSAGE,
    )
  }
}
